/**
 * Parser CSV minimale per l'import dei Job (professioni e-CF 3.0) dalla pagina
 * admin.
 *
 * Il CSV è interpretato lato client e trasformato in righe
 * `{ name, summary?, mission?, deliverables?, tasks?, kpi? }` da inviare a
 * `POST /api/jobs/import`. Il nome è la chiave: il backend crea i nomi nuovi e
 * aggiorna i campi testuali di quelli già presenti (non distruttivo).
 *
 * Formato atteso (coerente con `csv/e-cf 3.0/jobs.csv`):
 *  - prima riga di intestazione con i nomi delle colonne (case‑insensitive);
 *  - colonna `name` obbligatoria (alias: `nome`, `job`, `professione`);
 *  - colonne opzionali `summary` (alias: `description`, `descrizione`, `sintesi`),
 *    `mission` (alias: `missione`), `deliverables` (alias: `deliverable`),
 *    `tasks` (alias: `task`, `compiti`), `kpi`;
 *  - separatore virgola `,`; supporto dei campi tra virgolette doppie con
 *    escape a virgolette raddoppiate (`"a""b"` → `a"b`).
 *
 * Riusa lo stesso stile del parser competenze (`parseCompetencesCsv`).
 */
import {
  isLivello,
  type ImportJobCompetenceRow,
  type ImportJobRow,
} from '../shared/dto';

/** Errore sollevato quando il contenuto CSV non è utilizzabile. */
export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

type JobField = 'name' | 'summary' | 'mission' | 'deliverables' | 'tasks' | 'kpi';

/** Alias di intestazione accettati per ciascun campo (normalizzati lowercase). */
const HEADER_ALIASES: Record<JobField, readonly string[]> = {
  name: ['name', 'nome', 'job', 'professione'],
  summary: ['summary', 'description', 'descrizione', 'sintesi', 'desc'],
  mission: ['mission', 'missione'],
  deliverables: ['deliverables', 'deliverable'],
  tasks: ['tasks', 'task', 'compiti', 'attivita', 'attività'],
  kpi: ['kpi', 'kpis'],
};

/** Scompone una singola riga CSV in campi, gestendo le virgolette doppie. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1; // salta la virgoletta di escape
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/** Individua l'indice della colonna corrispondente a un campo, o -1. */
function findColumn(header: string[], field: JobField): number {
  const aliases = HEADER_ALIASES[field];
  return header.findIndex((h) => aliases.includes(h.trim().toLowerCase()));
}

/**
 * Interpreta il testo CSV in un elenco di righe Job pronte per l'import.
 *
 * @throws {CsvParseError} se il file è vuoto, manca la colonna `name`, o non
 *   contiene righe dati valide.
 */
export function parseJobsCsv(text: string): ImportJobRow[] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    throw new CsvParseError('Il file CSV è vuoto.');
  }

  const header = splitCsvLine(lines[0]);
  const nameIdx = findColumn(header, 'name');
  if (nameIdx === -1) {
    throw new CsvParseError('Colonna "name" mancante nell\'intestazione del CSV.');
  }
  const summaryIdx = findColumn(header, 'summary');
  const missionIdx = findColumn(header, 'mission');
  const deliverablesIdx = findColumn(header, 'deliverables');
  const tasksIdx = findColumn(header, 'tasks');
  const kpiIdx = findColumn(header, 'kpi');

  const cellAt = (cells: string[], idx: number): string =>
    idx === -1 ? '' : (cells[idx] ?? '').trim();

  const rows: ImportJobRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const name = (cells[nameIdx] ?? '').trim();
    if (name === '') continue; // salta righe senza nome

    const row: ImportJobRow = { name };
    const summary = cellAt(cells, summaryIdx);
    if (summary !== '') row.summary = summary;
    const mission = cellAt(cells, missionIdx);
    if (mission !== '') row.mission = mission;
    const deliverables = cellAt(cells, deliverablesIdx);
    if (deliverables !== '') row.deliverables = deliverables;
    const tasks = cellAt(cells, tasksIdx);
    if (tasks !== '') row.tasks = tasks;
    const kpi = cellAt(cells, kpiIdx);
    if (kpi !== '') row.kpi = kpi;

    rows.push(row);
  }

  if (rows.length === 0) {
    throw new CsvParseError('Nessuna riga job valida nel CSV.');
  }

  return rows;
}

/** Alias di intestazione per il file di relazioni job↔competenze. */
const JOB_COMPETENCE_ALIASES = {
  competenceName: ['name', 'nome', 'competenza', 'competence'],
  level: ['level', 'livello', 'required', 'required level'],
} as const;

/**
 * Interpreta un file di relazioni Job → Competenza (tipicamente
 * `job-competences/<Nome Job>.csv`), con intestazione `name,level` dove `name` è
 * il nome della competenza e-CF e `level` il Livello richiesto (`e-0..e-5`).
 *
 * I valori di livello non riconosciuti vengono ignorati (la relazione resta
 * senza Livello richiesto). Le righe senza nome competenza vengono saltate.
 *
 * @throws {CsvParseError} se il file è vuoto o manca la colonna `name`.
 */
export function parseJobCompetencesCsv(text: string): ImportJobCompetenceRow[] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    throw new CsvParseError('Il file CSV è vuoto.');
  }

  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const nameIdx = header.findIndex((h) =>
    JOB_COMPETENCE_ALIASES.competenceName.includes(
      h as (typeof JOB_COMPETENCE_ALIASES.competenceName)[number],
    ),
  );
  if (nameIdx === -1) {
    throw new CsvParseError('Colonna "name" mancante nell\'intestazione del CSV.');
  }
  const levelIdx = header.findIndex((h) =>
    JOB_COMPETENCE_ALIASES.level.includes(
      h as (typeof JOB_COMPETENCE_ALIASES.level)[number],
    ),
  );

  const rows: ImportJobCompetenceRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const competenceName = (cells[nameIdx] ?? '').trim();
    if (competenceName === '') continue;

    const row: ImportJobCompetenceRow = { competenceName };
    if (levelIdx !== -1) {
      const raw = (cells[levelIdx] ?? '').trim().toLowerCase();
      if (isLivello(raw)) row.level = raw;
    }
    rows.push(row);
  }

  return rows;
}
